import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import { useAuth } from '../context/AuthContext';
import { useTabIdentity } from '../hooks/useTabIdentity';

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const tabIdentity = useMemo(
    () => (article?.title ? { title: article.title, badge: 'С' } : null),
    [article?.title],
  );
  useTabIdentity(tabIdentity);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/articles/${encodeURIComponent(slug)}/`);
        if (!cancelled) setArticle(res.data);
      } catch {
        if (!cancelled) {
          setArticle(null);
          setError('Статья не найдена.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="articles-page">
        <p className="text-secondary">Загрузка…</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="articles-page">
        <p className="form-error">{error || 'Статья не найдена.'}</p>
        <Link to="/articles" className="btn btn-outline btn-sm">
          К списку статей
        </Link>
      </div>
    );
  }

  return (
    <article className="articles-page article-detail">
      <div className="article-detail-nav">
        <Link to="/articles" className="text-secondary">
          ← Статьи
        </Link>
        {isAdmin && (
          <Link
            to={`/admin/articles?slug=${encodeURIComponent(article.slug)}`}
            className="btn btn-outline btn-sm"
          >
            Править
          </Link>
        )}
      </div>

      {article.cover_url ? (
        <img src={article.cover_url} alt="" className="article-detail-cover" />
      ) : null}

      <header className="article-detail-header">
        <h1>{article.title}</h1>
        <p className="article-detail-meta text-secondary">
          {formatDate(article.published_at)}
          {article.author_name ? ` · ${article.author_name}` : ''}
        </p>
        {article.summary ? <p className="article-detail-summary">{article.summary}</p> : null}
      </header>

      <div className="article-detail-body">
        <MarkdownText text={article.body_md || ''} />
      </div>
    </article>
  );
}
